import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import './WorkoutModal.css';

// Nested exercise/profile dialogs share one lock and restore the original page position.
const openDialogs = [];
const dialogListeners = new Set();
let restorePage = null;
let nextDialogId = 0;
const notifyDialogs = () => dialogListeners.forEach(listener => listener());

function lockPage() {
  const body = document.body;
  const root = document.documentElement;
  const prior = { position: body.style.position, top: body.style.top, left: body.style.left, width: body.style.width, overflow: body.style.overflow, rootOverflow: root.style.overflow };
  const x = window.scrollX;
  const y = window.scrollY;
  Object.assign(body.style, { position: 'fixed', top: `${-y}px`, left: `${-x}px`, width: '100%', overflow: 'hidden' });
  root.style.overflow = 'hidden';
  return () => {
    Object.assign(body.style, { position: prior.position, top: prior.top, left: prior.left, width: prior.width, overflow: prior.overflow });
    root.style.overflow = prior.rootOverflow;
    window.scrollTo({ left: x, top: y, behavior: 'instant' });
  };
}

function focusableElements(panel) {
  return [...panel.querySelectorAll('button, a[href], input, select, textarea, [tabindex]')].filter(element => {
    if (element.disabled || element.tabIndex < 0 || element.type === 'hidden') return false;
    for (let current = element; current && current !== panel; current = current.parentElement) {
      const style = window.getComputedStyle(current);
      if (current.hidden || style.display === 'none' || style.visibility === 'hidden') return false;
    }
    return true;
  });
}

export default function WorkoutModal({ open, onClose, title, size = 'max-w-4xl', children, footer = null }) {
  const [id] = useState(() => `workout-dialog-${++nextDialogId}`);
  const [, updateStack] = useState(0);
  const panelRef = useRef(null);
  const layerRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const isTop = openDialogs[openDialogs.length - 1] === id;

  useEffect(() => {
    // Setting the DOM attribute works with both React 18 and React 19.
    layerRef.current?.toggleAttribute('inert', !isTop);
    if (open && isTop && !panelRef.current?.contains(document.activeElement)) {
      panelRef.current?.focus({ preventScroll: true });
    }
  }, [open, isTop]);

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    if (!openDialogs.length) restorePage = lockPage();
    openDialogs.push(id);
    const listener = () => updateStack(value => value + 1);
    dialogListeners.add(listener);
    notifyDialogs();
    const focusTimer = window.setTimeout(() => {
      if (openDialogs[openDialogs.length - 1] === id && !panelRef.current?.contains(document.activeElement)) {
        panelRef.current?.focus({ preventScroll: true });
      }
    }, 0);
    const keydown = event => {
      if (openDialogs[openDialogs.length - 1] !== id || event.defaultPrevented) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
      } else if (event.key === 'Tab' && panelRef.current) {
        const elements = focusableElements(panelRef.current);
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (!first) { event.preventDefault(); panelRef.current.focus(); }
        else if (event.shiftKey && (document.activeElement === first || !elements.includes(document.activeElement))) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !elements.includes(document.activeElement))) { event.preventDefault(); first.focus(); }
      }
    };
    const containFocus = event => {
      if (openDialogs[openDialogs.length - 1] === id && panelRef.current && !panelRef.current.contains(event.target)) {
        panelRef.current.focus({ preventScroll: true });
      }
    };
    document.addEventListener('keydown', keydown);
    document.addEventListener('focusin', containFocus);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', keydown);
      document.removeEventListener('focusin', containFocus);
      dialogListeners.delete(listener);
      const index = openDialogs.indexOf(id);
      if (index !== -1) openDialogs.splice(index, 1);
      if (!openDialogs.length) { restorePage?.(); restorePage = null; }
      // Native browsers reject focus inside an inert parent. React's next render
      // has not run yet, so reactivate the returning layer before restoring focus.
      const nextId = openDialogs[openDialogs.length - 1];
      const returningPanel = nextId ? document.getElementById(`${nextId}-title`)?.closest('[role="dialog"]') : null;
      returningPanel?.parentElement.removeAttribute('inert');
      returningPanel?.parentElement.removeAttribute('aria-hidden');
      notifyDialogs();
      if (opener?.isConnected && (!nextId || returningPanel?.contains(opener))) opener.focus({ preventScroll: true });
    };
  }, [open, id]);

  if (!open) return null;
  return createPortal(
    <div ref={layerRef} className="workout-dialog-layer" style={{ zIndex: 1000 + Math.max(0, openDialogs.indexOf(id)) * 10 }} aria-hidden={isTop ? undefined : true}>
      <div ref={panelRef} role="dialog" aria-modal={isTop ? true : undefined} aria-labelledby={`${id}-title`} data-has-footer={footer ? 'true' : 'false'} tabIndex={-1} className={`workout-dialog-panel ${size}`}>
        <div className="workout-dialog-header">
          <h2 id={`${id}-title`}>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="workout-dialog-close"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        <div data-modal-scroll className="workout-dialog-scroll">{children}</div>
        {footer && <div className="workout-dialog-footer">{footer}</div>}
      </div>
    </div>, document.body,
  );
}
